import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Box, Typography, Paper, Button, TextField, Chip, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow,
  Grid, Divider, Drawer, IconButton, Snackbar, Alert, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox,
  FormControlLabel, Avatar, Fab, Tabs, Tab,
  Select, MenuItem, FormControl, InputLabel, Switch, FormGroup,
} from '@mui/material';
import {
  Sync as SyncIcon, Search as SearchIcon, Close as CloseIcon,
  OpenInNew as OpenInNewIcon, Email as EmailIcon, Forum as ForumIcon,
  ConfirmationNumber as TicketIcon,
  HourglassEmpty as HourglassEmptyIcon, Refresh as RefreshIcon,
  Lock as LockIcon, LockOpen as LockOpenIcon,
  PersonAdd as AssignIcon, Code as DevIcon, HeadsetMic as SupportIcon,
  AutoAwesome as AiIcon, Download as DownloadIcon,
  PauseCircle as HoldIcon, TrendingUp as AnalyticsIcon,
  ArrowUpward as AscIcon, ArrowDownward as DescIcon,
  SelectAll as SelectAllIcon, CheckBox as CheckBoxIcon,
  Today as TodayIcon, Schedule as ScheduleIcon,
  Save as SaveIcon, StickyNote2 as NoteIcon,
  AttachFile as AttachIcon, InsertDriveFile as FileIcon,
  Image as ImageIcon, PictureAsPdf as PdfIcon,
  ChevronLeft as ChevronLeftIcon,
  Reply as ReplyIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import nablSupportService from '../../services/nablSupportService';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const REPLY_QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['link', 'image'],
    ['blockquote'],
    ['clean'],
  ],
  clipboard: { matchVisual: false },
};
const REPLY_QUILL_FORMATS = [
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'indent',
  'align',
  'link', 'image',
  'blockquote',
];

// ── Design tokens — warm nude palette ─────────────────────────────────────────
const T = {
  canvas:      '#F8F5F1',
  surface:     '#FFFFFF',
  surfaceAlt:  '#F3EFE9',
  surfaceWarm: '#FAF7F4',
  border:      '#E4DCCF',
  borderLight: '#EDE8E1',
  textPri:     '#2D2118',
  textSec:     '#6B5B50',
  textDis:     '#A99B8E',
  navy:        '#00024c',
  navyLight:   '#0A0A6B',
  accent:      '#C84B2F',
  // age row tints
  ageCritBg:   '#FDF3F0', ageCritBd: '#D4917E',
  ageWarnBg:   '#FDF8EF', ageWarnBd: '#C9A45A',
  ageCautBg:   '#FDFBF0', ageCautBd: '#C5B050',
  ageFreshBg:  '#F3FAF5', ageFreshBd: '#7DBD90',
  // thread direction
  threadInBg:  '#F7F5FF', threadInBd: '#9178D4',
  threadOutBg: '#F3FAF6', threadOutBd: '#68A87A',
};

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_COLOR = { Open: 'error', Closed: 'success', 'On Hold': 'warning', Replied: 'info' };
const AVATAR_COLORS = ['#5C6BC0', '#4CAF82', '#E08A3C', '#9C6BC4', '#C45F5F', '#3D9EAA'];
const SLA_DAYS = { Development: 5, Support: 2 };
const PIE_COLORS = ['#C45F5F', '#E08A3C', '#4CAF82', '#5C6BC0', '#9C6BC4', '#3D9EAA'];
const AUTO_REFRESH_MS = 5 * 60 * 1000;

// ── Helpers ───────────────────────────────────────────────────────────────────
const getAgePriority = (createdTime) => {
  if (!createdTime) return { bg: T.surface, border: T.border, label: '?', chipColor: 'default' };
  const days = (Date.now() - new Date(createdTime).getTime()) / 86400000;
  if (days >= 7) return { bg: T.ageCritBg,  border: T.ageCritBd,  label: `${Math.floor(days)}d`,      chipColor: 'error' };
  if (days >= 3) return { bg: T.ageWarnBg,  border: T.ageWarnBd,  label: `${Math.floor(days)}d`,      chipColor: 'warning' };
  if (days >= 1) return { bg: T.ageCautBg,  border: T.ageCautBd,  label: `${Math.floor(days)}d`,      chipColor: 'default' };
  return             { bg: T.ageFreshBg, border: T.ageFreshBd, label: `${Math.floor(days * 24)}h`, chipColor: 'success' };
};

const getSLAInfo = (createdTime, ticketType) => {
  const slaDays = SLA_DAYS[ticketType];
  if (!slaDays || !createdTime) return null;
  const days = (Date.now() - new Date(createdTime).getTime()) / 86400000;
  const remaining = slaDays - days;
  if (remaining <= 0) return { label: `${Math.abs(Math.floor(remaining))}d over`, color: 'error' };
  if (remaining <= slaDays * 0.4) return { label: `${Math.floor(remaining)}d left`, color: 'warning' };
  return null;
};

const toggleSort = (prev, field) => ({
  field,
  dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc',
});

const applySort = (tickets, field, dir) => {
  if (!field) return tickets;
  return [...tickets].sort((a, b) => {
    let va = a[field], vb = b[field];
    if (field === 'createdTime') { va = new Date(va); vb = new Date(vb); }
    if (field === 'threadCount') { va = Number(va); vb = Number(vb); }
    if (va < vb) return dir === 'asc' ? -1 : 1;
    if (va > vb) return dir === 'asc' ? 1 : -1;
    return 0;
  });
};

// ── EmailFrame — sandboxed iframe renderer (Gmail-style, no horizontal scroll) ─
const EmailFrame = ({ html }) => {
  const ref = React.useRef(null);
  const handleLoad = () => {
    const iframe = ref.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc?.body) iframe.style.height = Math.min(doc.body.scrollHeight + 16, 520) + 'px';
  };
  const srcDoc = `<!DOCTYPE html><html><head><style>
    body{margin:0;padding:8px;font-family:Arial,sans-serif;font-size:13px;line-height:1.5;
         overflow-x:hidden;word-break:break-word;color:#222;}
    img{max-width:100%!important;height:auto;}
    table{max-width:100%!important;word-break:break-word;}
    pre,code{white-space:pre-wrap;word-break:break-all;}
    a{color:#1976d2;}
    blockquote{border-left:3px solid #ccc;margin:4px 0;padding:0 8px;color:#555;}
  </style></head><body>${html}</body></html>`;
  return (
    <iframe ref={ref} srcDoc={srcDoc} sandbox="allow-same-origin"
      style={{ width: '100%', border: 'none', minHeight: 60, maxHeight: 520, display: 'block', borderRadius: 4, background: '#f8fafc' }}
      onLoad={handleLoad} title="email-content" />
  );
};

// ── StatCard ──────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, color }) => (
  <Paper elevation={0} sx={{
    p: 1.75, borderRadius: 2.5, height: '100%',
    border: `1px solid ${T.borderLight}`,
    borderTop: `3px solid ${color}`,
    bgcolor: T.surface,
    boxShadow: '0 2px 8px rgba(45,33,24,0.06)',
    transition: 'box-shadow 0.2s',
    '&:hover': { boxShadow: '0 4px 16px rgba(45,33,24,0.10)' },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: T.textSec, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1, color: T.textPri }}>{value ?? '—'}</Typography>
      </Box>
      <Box sx={{ bgcolor: color + '18', borderRadius: 2, p: 0.9, display: 'flex', color, flexShrink: 0, mt: 0.25 }}>{React.cloneElement(icon, { sx: { fontSize: 20 } })}</Box>
    </Box>
  </Paper>
);

// ── WorkloadMini ──────────────────────────────────────────────────────────────
const WorkloadMini = ({ assignmentsMap, openTicketNums }) => {
  const data = useMemo(() => {
    const counts = {};
    for (const [tn, users] of Object.entries(assignmentsMap)) {
      if (!openTicketNums.has(tn)) continue;
      for (const u of users) counts[u.name] = (counts[u.name] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }));
  }, [assignmentsMap, openTicketNums]);

  if (!data.length) return null;
  const max = Math.max(...data.map(d => d.count), 1);

  return (
    <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2.5, mb: 2, border: `1px solid ${T.borderLight}`, bgcolor: T.surfaceWarm }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: T.textSec, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 1.25 }}>
        Team Workload — Open Tickets
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {data.map((d, i) => (
          <Box key={d.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 130 }}>
            <Avatar sx={{ width: 24, height: 24, fontSize: '0.62rem', fontWeight: 700, bgcolor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>{d.name[0]}</Avatar>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: T.textPri }}>{d.name}</Typography>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>{d.count}</Typography>
              </Box>
              <Box sx={{ height: 4, bgcolor: T.border, borderRadius: 4 }}>
                <Box sx={{ height: '100%', width: `${(d.count / max) * 100}%`, bgcolor: AVATAR_COLORS[i % AVATAR_COLORS.length], borderRadius: 4, transition: 'width 0.4s ease' }} />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

// ── SortableHeader ────────────────────────────────────────────────────────────
const SortableHeader = ({ field, label, width, center, sortField, sortDir, onSort }) => (
  <TableCell
    sx={{ fontWeight: 700, py: 0.85, width, fontSize: '0.68rem', cursor: 'pointer', userSelect: 'none',
      textAlign: center ? 'center' : 'left', color: T.textSec, textTransform: 'uppercase', letterSpacing: '0.05em',
      '&:hover': { color: T.textPri } }}
    onClick={() => onSort(field)}
  >
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {label}
      {sortField === field && (sortDir === 'asc'
        ? <AscIcon sx={{ fontSize: 10 }} />
        : <DescIcon sx={{ fontSize: 10 }} />)}
    </Box>
  </TableCell>
);

// ── TicketTable ───────────────────────────────────────────────────────────────
const TicketTable = ({
  title, headerColor, tickets, loading, total, page, totalPages, pageSize,
  onPageChange, onRowClick, onStatusAction, onTypeAction, statusActionLoading,
  typeActionLoading, assignmentsMap, onAssign,
  selectedTickets, onToggleSelect, onSelectAll,
  sortField, sortDir, onSort, onDelete, readTickets,
}) => {
  const sorted = useMemo(() => applySort(tickets, sortField, sortDir), [tickets, sortField, sortDir]);
  const allSelected = tickets.length > 0 && tickets.every(t => selectedTickets.has(t.ticketNumber));

  return (
    <Paper elevation={0} sx={{ borderRadius: 2.5, display: 'flex', flexDirection: 'column', border: `1px solid ${T.borderLight}`, overflow: 'hidden', boxShadow: '0 2px 8px rgba(45,33,24,0.06)' }}>
      <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${T.borderLight}`, bgcolor: T.surfaceAlt, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 3, height: 18, borderRadius: 2, bgcolor: headerColor }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: T.textPri }}>{title}</Typography>
          <Chip label={total} size="small" sx={{ fontWeight: 700, height: 18, fontSize: '0.65rem', bgcolor: headerColor + '20', color: headerColor, border: 'none' }} />
        </Box>
        {tickets.length > 0 && (
          <Tooltip title={allSelected ? 'Deselect all on page' : 'Select all on page'}>
            <IconButton size="small" onClick={() => onSelectAll(tickets, allSelected)} sx={{ color: allSelected ? headerColor : T.textDis }}>
              <SelectAllIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={26} sx={{ color: headerColor }} /></Box>
      ) : (
        <>
          <Box sx={{ overflowY: 'auto', maxHeight: 480 }}>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
                  <TableCell sx={{ width: '3%', py: 0.85, px: 0.5, borderBottom: 'none' }} />
                  <TableCell sx={{ fontWeight: 700, py: 0.85, width: '3%', fontSize: '0.68rem', color: T.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: 'none' }}>#</TableCell>
                  <SortableHeader field="subject" label="Ticket / Subject" width="28%" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                  <SortableHeader field="createdTime" label="From / Date" width="19%" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                  <SortableHeader field="status" label="Status" width="10%" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                  <TableCell sx={{ fontWeight: 700, py: 0.85, width: '9%', fontSize: '0.68rem', color: T.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: 'none' }}>SLA</TableCell>
                  <SortableHeader field="threadCount" label="Msgs" width="7%" center sortField={sortField} sortDir={sortDir} onSort={onSort} />
                  <TableCell sx={{ fontWeight: 700, py: 0.85, width: '9%', fontSize: '0.68rem', color: T.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: 'none' }}>Assigned</TableCell>
                  <TableCell sx={{ fontWeight: 700, py: 0.85, width: '12%', fontSize: '0.68rem', color: T.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center', borderBottom: 'none' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((t, idx) => {
                  const assigned = assignmentsMap[t.ticketNumber] || [];
                  const isOpen = t.status === 'Open' || t.status === 'On Hold';
                  const age = getAgePriority(t.createdTime);
                  const sla = getSLAInfo(t.createdTime, t.ticket_type);
                  const isSelected = selectedTickets.has(t.ticketNumber);
                  const isRead = readTickets?.has(t.ticketNumber);
                  return (
                    <TableRow
                      key={t.ticketNumber}
                      hover
                      onClick={() => onRowClick(t)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isSelected ? '#EEF3FB' : age.bg,
                        borderLeft: `3px solid ${isSelected ? '#5C6BC0' : age.border}`,
                        transition: 'background-color 0.15s',
                        '&:hover': { bgcolor: isSelected ? '#E5EDF9' : age.bg, filter: 'brightness(0.975)' },
                        '& td': { borderBottomColor: T.borderLight },
                      }}
                    >
                      <TableCell sx={{ py: 0.6, px: 0.5 }} onClick={e => e.stopPropagation()}>
                        <Checkbox size="small" checked={isSelected} onChange={() => onToggleSelect(t.ticketNumber)} sx={{ p: 0.25, color: T.textDis }} />
                      </TableCell>
                      <TableCell sx={{ py: 0.85, color: T.textDis, fontSize: '0.68rem', fontWeight: 600 }}>
                        {(page - 1) * pageSize + idx + 1}
                      </TableCell>
                      <TableCell sx={{ py: 0.85 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                          {!isRead && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4A6FA5', flexShrink: 0 }} />}
                          <Typography sx={{ fontWeight: isRead ? 400 : 700, color: isRead ? T.textSec : T.textPri, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.subject}>
                            {t.subject || '—'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontWeight: 600, color: '#4A6FA5', fontSize: '0.62rem' }}>{t.ticketNumber}</Typography>
                          <Chip label={age.label} size="small" color={age.chipColor} sx={{ height: 15, fontSize: '0.56rem', fontWeight: 700 }} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.85 }}>
                        <Typography sx={{ fontSize: '0.7rem', color: T.textPri, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.email || t.fromEmailAddress}>
                          {t.email || t.fromEmailAddress || '—'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.64rem', color: T.textDis }}>
                          {t.createdTime ? new Date(t.createdTime).toLocaleDateString('en-IN') : ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.85 }}>
                        <Chip label={t.status || '?'} size="small" color={STATUS_COLOR[t.status] || 'default'} sx={{ height: 18, fontSize: '0.6rem' }} />
                      </TableCell>
                      <TableCell sx={{ py: 0.85 }}>
                        {sla
                          ? <Chip label={sla.label} size="small" color={sla.color} variant="outlined" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700 }} />
                          : <Typography sx={{ fontSize: '0.62rem', color: T.textDis }}>—</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 0.85, textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                          <ForumIcon sx={{ fontSize: 11, color: T.textDis }} />
                          <Typography sx={{ fontSize: '0.62rem', color: T.textDis }}>{t.threadCount || 0}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.85 }}>
                        <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                          {assigned.length === 0
                            ? <Typography sx={{ fontSize: '0.62rem', color: T.textDis }}>—</Typography>
                            : assigned.map((u, i) => (
                              <Tooltip key={u.user_id} title={u.name}>
                                <Avatar sx={{ width: 22, height: 22, fontSize: '0.6rem', fontWeight: 700, bgcolor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>{u.name[0].toUpperCase()}</Avatar>
                              </Tooltip>
                            ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.85 }} onClick={e => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25 }}>
                          {t.webUrl && (
                            <Tooltip title="Open in Gmail">
                              <IconButton size="small" sx={{ p: 0.4, color: T.textDis, '&:hover': { color: T.textSec } }} onClick={() => window.open(t.webUrl, '_blank')}>
                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Assign users">
                            <IconButton size="small" sx={{ p: 0.4, color: '#5C6BC0', '&:hover': { bgcolor: '#EEF0FA' } }} onClick={() => onAssign(t)}>
                              <AssignIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t.ticket_type === 'Development' ? 'Move to Support' : 'Mark as Development'}>
                            <span>
                              <IconButton size="small" sx={{ p: 0.4, color: t.ticket_type === 'Development' ? '#B07A2A' : '#8B5AA8', '&:hover': { bgcolor: '#F5F0FB' } }}
                                disabled={typeActionLoading === t.ticketNumber}
                                onClick={() => onTypeAction(t.ticketNumber, t.ticket_type === 'Development' ? 'Support' : 'Development')}>
                                {typeActionLoading === t.ticketNumber ? <CircularProgress size={12} /> : t.ticket_type === 'Development' ? <SupportIcon sx={{ fontSize: 14 }} /> : <DevIcon sx={{ fontSize: 14 }} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={isOpen ? 'Close ticket' : 'Reopen ticket'}>
                            <span>
                              <IconButton size="small" sx={{ p: 0.4, color: isOpen ? '#3D7A52' : '#B05050', '&:hover': { bgcolor: isOpen ? '#EDF6F1' : '#FCEAEA' } }}
                                disabled={statusActionLoading === t.ticketNumber}
                                onClick={() => onStatusAction(t.ticketNumber, isOpen ? 'Closed' : 'Open')}>
                                {statusActionLoading === t.ticketNumber ? <CircularProgress size={12} /> : isOpen ? <LockIcon sx={{ fontSize: 14 }} /> : <LockOpenIcon sx={{ fontSize: 14 }} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete ticket">
                            <IconButton size="small" sx={{ p: 0.4, color: '#B05050', '&:hover': { bgcolor: '#FCEAEA' } }} onClick={() => onDelete(t)}>
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!tickets.length && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5, borderBottom: 'none' }}>
                      <Typography sx={{ fontSize: '0.82rem', color: T.textDis }}>No tickets found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.75, py: 0.85, borderTop: `1px solid ${T.borderLight}`, bgcolor: T.surfaceWarm }}>
            <Button size="small" disabled={page === 1} onClick={() => onPageChange(page - 1)} sx={{ minWidth: 0, px: 1.25, fontSize: '0.72rem', color: T.textSec }}>‹ Prev</Button>
            <Typography sx={{ fontSize: '0.72rem', color: T.textDis }}>Page {page} / {totalPages || 1}</Typography>
            <Button size="small" disabled={page >= (totalPages || 1)} onClick={() => onPageChange(page + 1)} sx={{ minWidth: 0, px: 1.25, fontSize: '0.72rem', color: T.textSec }}>Next ›</Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

// ── AnalyticsTab ──────────────────────────────────────────────────────────────
const AnalyticsTab = ({ analytics, loading }) => {
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!analytics) return <Typography align="center" color="text.secondary" sx={{ py: 8 }}>No data yet. Click "Reload Analytics".</Typography>;

  const { dailyCreated = [], statusBreakdown = [], typeBreakdown = [], ageDistribution = [], workloadPerAssignee = [] } = analytics;
  const AGE_ORDER = ['< 1d', '1-3d', '3-7d', '7d+'];
  const ageSorted = AGE_ORDER.map(b => ({ name: b, count: ageDistribution.find(a => a.bucket === b)?.count || 0 }));
  const AGE_COLORS = ['#2e7d32', '#f9a825', '#ed6c02', '#d32f2f'];

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, mb: 2, border: `1px solid ${T.borderLight}`, boxShadow: '0 2px 8px rgba(45,33,24,0.05)' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: T.textPri, mb: 2 }}>Tickets Created — Last 30 Days</Typography>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={dailyCreated} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={d => d ? String(d).slice(5) : ''} />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <ReTooltip />
            <Bar dataKey="count" name="Tickets" fill="#1976d2" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${T.borderLight}`, boxShadow: '0 2px 8px rgba(45,33,24,0.05)' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: T.textPri, mb: 1 }}>By Status</Typography>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={statusBreakdown} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={65} label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                  {statusBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <ReTooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${T.borderLight}`, boxShadow: '0 2px 8px rgba(45,33,24,0.05)' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: T.textPri, mb: 1 }}>Dev vs Support</Typography>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={typeBreakdown} dataKey="count" nameKey="ticket_type" cx="50%" cy="50%" outerRadius={65} label={({ ticket_type, percent }) => `${ticket_type} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                  {typeBreakdown.map((_, i) => <Cell key={i} fill={i === 0 ? '#7b1fa2' : '#0288d1'} />)}
                </Pie>
                <ReTooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${T.borderLight}`, boxShadow: '0 2px 8px rgba(45,33,24,0.05)' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: T.textPri, mb: 1 }}>Age Distribution (Open)</Typography>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={ageSorted} margin={{ top: 5, right: 5, left: -25, bottom: 5 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <ReTooltip />
                <Bar dataKey="count" name="Tickets" radius={[3, 3, 0, 0]}>
                  {ageSorted.map((_, i) => <Cell key={i} fill={AGE_COLORS[i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {workloadPerAssignee.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${T.borderLight}`, boxShadow: '0 2px 8px rgba(45,33,24,0.05)' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: T.textPri, mb: 1 }}>Workload per Assignee (Open Tickets)</Typography>
          <ResponsiveContainer width="100%" height={Math.max(160, workloadPerAssignee.length * 36)}>
            <BarChart data={workloadPerAssignee} layout="vertical" margin={{ top: 5, right: 20, left: 50, bottom: 5 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} />
              <ReTooltip />
              <Bar dataKey="count" name="Open Tickets" radius={[0, 3, 3, 0]}>
                {workloadPerAssignee.map((_, i) => <Cell key={i} fill={AVATAR_COLORS[i % AVATAR_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Box>
  );
};

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function NablSupportDashboard() {
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [statusActionLoading, setStatusActionLoading] = useState(null);
  const [typeActionLoading, setTypeActionLoading] = useState(null);

  const [devTickets, setDevTickets] = useState([]);
  const [devTotal, setDevTotal] = useState(0);
  const [devTotalPages, setDevTotalPages] = useState(1);
  const [devPage, setDevPage] = useState(1);
  const [devLoading, setDevLoading] = useState(false);
  const [devSort, setDevSort] = useState({ field: 'createdTime', dir: 'asc' });

  const [supTickets, setSupTickets] = useState([]);
  const [supTotal, setSupTotal] = useState(0);
  const [supTotalPages, setSupTotalPages] = useState(1);
  const [supPage, setSupPage] = useState(1);
  const [supLoading, setSupLoading] = useState(false);
  const [supSort, setSupSort] = useState({ field: 'createdTime', dir: 'asc' });

  const [holdTickets, setHoldTickets] = useState([]);
  const [holdTotal, setHoldTotal] = useState(0);
  const [holdTotalPages, setHoldTotalPages] = useState(1);
  const [holdPage, setHoldPage] = useState(1);
  const [holdLoading, setHoldLoading] = useState(false);
  const [holdSort, setHoldSort] = useState({ field: 'createdTime', dir: 'asc' });

  const [closedTickets, setClosedTickets] = useState([]);
  const [closedTotal, setClosedTotal] = useState(0);
  const [closedTotalPages, setClosedTotalPages] = useState(1);
  const [closedPage, setClosedPage] = useState(1);
  const [closedLoading, setClosedLoading] = useState(false);
  const [closedSort, setClosedSort] = useState({ field: 'createdTime', dir: 'asc' });

  const [search, setSearch] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pageSize] = useState(20);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [allUsers, setAllUsers] = useState([]);
  const [assignmentsMap, setAssignmentsMap] = useState({});

  const [assignDialog, setAssignDialog] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [assigning, setAssigning] = useState(false);

  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [bulkClosing, setBulkClosing] = useState(false);
  const [bulkAssignDialog, setBulkAssignDialog] = useState(false);
  const [bulkSelectedUserIds, setBulkSelectedUserIds] = useState([]);
  const [bulkAssigning, setBulkAssigning] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);

  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  const [closeTicketDialog, setCloseTicketDialog] = useState(false);
  const [closeCorrectiveAction, setCloseCorrectiveAction] = useState('');
  const [closeCorrectiveActionTaken, setCloseCorrectiveActionTaken] = useState('');
  const [closeTicketLoading, setCloseTicketLoading] = useState(false);

  const [attachmentLoading, setAttachmentLoading] = useState(null);
  const [imagePopup, setImagePopup] = useState({ open: false, url: '', filename: '' });
  const [sidebarSearch, setSidebarSearch] = useState('');

  const [readTickets, setReadTickets] = useState(new Set());

  const [aiOpen, setAiOpen] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProvider, setAiProvider] = useState('');
  const aiBottomRef = useRef(null);

  const [sidebarTickets, setSidebarTickets] = useState([]);

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [replyOpen, setReplyOpen] = useState(false);
  const [replyTo, setReplyTo] = useState('');
  const [replyCc, setReplyCc] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [replySending, setReplySending] = useState(false);

  const [replyFlowActive, setReplyFlowActive] = useState(false);
  const [replyRemarksDialog, setReplyRemarksDialog] = useState(false);
  const [replyRemarks, setReplyRemarks] = useState('');
  const [remarksSaving, setRemarksSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const showSnackbar = (msg, sev = 'info') => setSnackbar({ open: true, message: msg, severity: sev });

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const buildFilters = useCallback(() => ({
    search, assigneeId: assigneeFilter, dateFrom, dateTo,
  }), [search, assigneeFilter, dateFrom, dateTo]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try { const r = await nablSupportService.getStats(); if (r.data?.success) setStats(r.data.data || {}); }
    catch (e) { console.error(e); } finally { setStatsLoading(false); }
  }, []);

  const fetchDevTickets = useCallback(async (pg = 1) => {
    setDevLoading(true);
    try {
      const r = await nablSupportService.getTickets({ page: pg, pageSize, ticketType: 'Development', status: 'Open', ...buildFilters() });
      if (r.data?.success) { setDevTickets(r.data.data || []); setDevTotal(r.data.total || 0); setDevTotalPages(r.data.totalPages || 1); setDevPage(pg); }
    } catch (e) { showSnackbar('Failed to load development tickets', 'error'); }
    finally { setDevLoading(false); }
  }, [pageSize, buildFilters]);

  const fetchSupTickets = useCallback(async (pg = 1) => {
    setSupLoading(true);
    try {
      const r = await nablSupportService.getTickets({ page: pg, pageSize, ticketType: 'Support', status: 'Open', ...buildFilters() });
      if (r.data?.success) { setSupTickets(r.data.data || []); setSupTotal(r.data.total || 0); setSupTotalPages(r.data.totalPages || 1); setSupPage(pg); }
    } catch (e) { showSnackbar('Failed to load support tickets', 'error'); }
    finally { setSupLoading(false); }
  }, [pageSize, buildFilters]);

  const fetchHoldTickets = useCallback(async (pg = 1) => {
    setHoldLoading(true);
    try {
      const r = await nablSupportService.getTickets({ page: pg, pageSize, status: 'On Hold', ...buildFilters() });
      if (r.data?.success) { setHoldTickets(r.data.data || []); setHoldTotal(r.data.total || 0); setHoldTotalPages(r.data.totalPages || 1); setHoldPage(pg); }
    } catch (e) { showSnackbar('Failed to load on-hold tickets', 'error'); }
    finally { setHoldLoading(false); }
  }, [pageSize, buildFilters]);

  const fetchClosedTickets = useCallback(async (pg = 1) => {
    setClosedLoading(true);
    try {
      const r = await nablSupportService.getTickets({ page: pg, pageSize, status: 'Closed', ...buildFilters() });
      if (r.data?.success) { setClosedTickets(r.data.data || []); setClosedTotal(r.data.total || 0); setClosedTotalPages(r.data.totalPages || 1); setClosedPage(pg); }
    } catch (e) { showSnackbar('Failed to load closed tickets', 'error'); }
    finally { setClosedLoading(false); }
  }, [pageSize, buildFilters]);

  const fetchAssignments = useCallback(async () => {
    try { const r = await nablSupportService.getAllAssignments(); if (r.data?.success) setAssignmentsMap(r.data.data || {}); }
    catch (e) { console.error(e); }
  }, []);

  const fetchUsers = useCallback(async () => {
    try { const r = await nablSupportService.getUsers(); if (r.data?.success) setAllUsers(r.data.data || []); }
    catch (e) { console.error(e); }
  }, []);

    const fetchSidebarTickets = useCallback(async () => {
    try {
      const [openRes, holdRes] = await Promise.all([
        nablSupportService.getTickets({ pageSize: 500, status: 'Open' }),
        nablSupportService.getTickets({ pageSize: 500, status: 'On Hold' }),
      ]);
      const open = openRes.data?.success ? openRes.data.data || [] : [];
      const hold = holdRes.data?.success ? holdRes.data.data || [] : [];
      const seen = new Set();
      const combined = [...open, ...hold].filter(t => {
        if (seen.has(t.ticketNumber)) return false;
        seen.add(t.ticketNumber);
        return true;
      });
      setSidebarTickets(combined);
    } catch (e) { console.error(e); }
  }, []);

  const fetchAll = useCallback(() => {
    fetchStats(); fetchDevTickets(1); fetchSupTickets(1); fetchHoldTickets(1); fetchClosedTickets(1); fetchAssignments(); fetchSidebarTickets();
    setSelectedTickets(new Set());
  }, [fetchStats, fetchDevTickets, fetchSupTickets, fetchHoldTickets, fetchClosedTickets, fetchAssignments, fetchSidebarTickets]);



  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try { const r = await nablSupportService.getAnalytics(); if (r.data?.success) setAnalytics(r.data.data); }
    catch (e) { showSnackbar('Analytics load failed', 'error'); }
    finally { setAnalyticsLoading(false); }
  }, []);

  // Use ref so interval always calls latest fetchAll
  const fetchAllRef = useRef(fetchAll);
  useEffect(() => { fetchAllRef.current = fetchAll; }, [fetchAll]);

  useEffect(() => { fetchAll(); fetchUsers(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => fetchAllRef.current(), AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh]);

  useEffect(() => {
    if (activeTab === 1 && !analytics && !analyticsLoading) fetchAnalytics();
  }, [activeTab]); // eslint-disable-line

  useEffect(() => {
    const allTickets = [...devTickets, ...supTickets, ...holdTickets, ...closedTickets, ...sidebarTickets];
    const readFromDb = allTickets.filter(t => t.is_read).map(t => t.ticketNumber);
    if (!readFromDb.length) return;
    setReadTickets(prev => {
      const hasNew = readFromDb.some(tn => !prev.has(tn));
      if (!hasNew) return prev;
      const next = new Set(prev);
      readFromDb.forEach(tn => next.add(tn));
      return next;
    });
  }, [devTickets, supTickets, holdTickets, closedTickets, sidebarTickets]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSync = async () => {
    setSyncing(true);
    try { const r = await nablSupportService.syncTickets(); showSnackbar(r.data?.message || 'Sync complete', 'success'); fetchAll(); }
    catch (e) { showSnackbar('Sync failed: ' + (e.response?.data?.message || e.message), 'error'); }
    finally { setSyncing(false); }
  };

  const handleStatusAction = async (ticketNumber, newStatus) => {
    setStatusActionLoading(ticketNumber);
    try { await nablSupportService.updateTicketStatus(ticketNumber, newStatus); showSnackbar(`Ticket ${ticketNumber} → ${newStatus}`, 'success'); fetchAll(); }
    catch (e) { showSnackbar('Update failed: ' + (e.response?.data?.message || e.message), 'error'); }
    finally { setStatusActionLoading(null); }
  };

  const handleTypeAction = async (ticketNumber, newType) => {
    setTypeActionLoading(ticketNumber);
    try { await nablSupportService.updateTicketType(ticketNumber, newType); showSnackbar(`Ticket ${ticketNumber} → ${newType}`, 'success'); fetchAll(); }
    catch (e) { showSnackbar('Type update failed', 'error'); }
    finally { setTypeActionLoading(null); }
  };

  const handleOpenAssign = (ticket) => {
    setAssignTarget(ticket);
    setSelectedUserIds((assignmentsMap[ticket.ticketNumber] || []).map(u => u.user_id));
    setAssignDialog(true);
  };

  const handleAssignSubmit = async () => {
    if (!assignTarget) return;
    setAssigning(true);
    try {
      await nablSupportService.assignTicket(assignTarget.ticketNumber, selectedUserIds);
      showSnackbar(`Assigned to ${selectedUserIds.length} user(s)`, 'success');
      setAssignDialog(false);
      fetchAssignments();
      if (replyFlowActive) { setReplyRemarks(''); setReplyRemarksDialog(true); }
    }
    catch (e) { showSnackbar('Assignment failed', 'error'); }
    finally { setAssigning(false); }
  };

  const advanceToRemarks = () => {
    setAssignDialog(false);
    setReplyRemarks('');
    setReplyRemarksDialog(true);
  };

  const advanceToReply = async () => {
    if (replyRemarks.trim()) {
      setRemarksSaving(true);
      try { await nablSupportService.saveNote(selectedTicket.ticketNumber, replyRemarks); } catch (e) {}
      setRemarksSaving(false);
    }
    setReplyRemarksDialog(false);
    setReplyFlowActive(false);
    setReplyOpen(true);
  };

  const handleSaveNote = async () => {
    if (!selectedTicket) return;
    setNoteSaving(true);
    try {
      await nablSupportService.saveNote(selectedTicket.ticketNumber, noteText);
      showSnackbar('Note saved', 'success');
    } catch (e) {
      showSnackbar('Failed to save note', 'error');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleOpenCloseDialog = () => {
    if (!selectedTicket) return;
    const assigned = assignmentsMap[selectedTicket.ticketNumber] || [];
    if (assigned.length === 0) {
      showSnackbar('Assign at least one user before closing this ticket', 'warning');
      handleOpenAssign(selectedTicket);
      return;
    }
    setCloseCorrectiveAction(noteText);
    setCloseCorrectiveActionTaken('');
    setCloseTicketDialog(true);
  };

  const handleDeleteTicket = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await nablSupportService.deleteTicket(deleteTarget.ticketNumber);
      showSnackbar(`Ticket ${deleteTarget.ticketNumber} deleted`, 'success');
      setDeleteDialog(false);
      if (selectedTicket?.ticketNumber === deleteTarget.ticketNumber) setDrawerOpen(false);
      fetchAll();
    } catch (e) {
      showSnackbar('Delete failed: ' + (e.response?.data?.message || e.message), 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  const handleConfirmClose = async () => {
    if (!closeCorrectiveAction.trim()) {
      showSnackbar('Corrective Action is required', 'warning');
      return;
    }
    setCloseTicketLoading(true);
    try {
      await nablSupportService.updateTicketStatus(selectedTicket.ticketNumber, 'Closed', closeCorrectiveAction);
      showSnackbar(`Ticket ${selectedTicket.ticketNumber} closed`, 'success');
      setCloseTicketDialog(false);
      setNoteText(closeCorrectiveAction);
      setSelectedTicket(p => ({ ...p, status: 'Closed', notes: closeCorrectiveAction }));
      fetchAll();
    } catch (e) {
      showSnackbar('Close failed: ' + (e.response?.data?.message || e.message), 'error');
    } finally {
      setCloseTicketLoading(false);
    }
  };

  const handleCloseImagePopup = () => {
    URL.revokeObjectURL(imagePopup.url);
    setImagePopup({ open: false, url: '', filename: '' });
  };

  const handleDownloadAttachment = async (messageId, attachmentId, filename, mimeType) => {
    const key = `${messageId}_${attachmentId}`;
    setAttachmentLoading(key);
    try {
      const r = await nablSupportService.getAttachment(messageId, attachmentId, filename, mimeType);
      const blob = new Blob([r.data], { type: mimeType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      if (mimeType?.startsWith('image/')) {
        setImagePopup({ open: true, url, filename: filename || 'image' });
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'attachment';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      showSnackbar('Failed to download attachment', 'error');
    } finally {
      setAttachmentLoading(null);
    }
  };

  const handleRowClick = async (ticket) => {
    setSelectedTicket(ticket);
    setNoteText(ticket.notes || '');
    setDrawerOpen(true);
    setThreads([]);
    setThreadsLoading(true);
    if (!readTickets.has(ticket.ticketNumber)) {
      setReadTickets(prev => { const next = new Set(prev); next.add(ticket.ticketNumber); return next; });
      nablSupportService.markAsRead(ticket.ticketNumber).catch(e => console.error('markAsRead failed:', e));
    }
    try { const r = await nablSupportService.getTicketThreads(ticket.ticketNumber); if (r.data?.success) setThreads(r.data.data || []); }
    catch (e) { console.error(e); }
    finally { setThreadsLoading(false); }
  };

  const handleToggleSelect = (ticketNumber) => {
    setSelectedTickets(prev => { const n = new Set(prev); n.has(ticketNumber) ? n.delete(ticketNumber) : n.add(ticketNumber); return n; });
  };

  const handleSelectAll = (tickets, allSelected) => {
    setSelectedTickets(prev => { const n = new Set(prev); tickets.forEach(t => allSelected ? n.delete(t.ticketNumber) : n.add(t.ticketNumber)); return n; });
  };

  const handleBulkClose = async () => {
    setBulkClosing(true);
    try { await nablSupportService.bulkUpdateStatus([...selectedTickets], 'Closed'); showSnackbar(`Closed ${selectedTickets.size} tickets`, 'success'); setSelectedTickets(new Set()); fetchAll(); }
    catch (e) { showSnackbar('Bulk close failed: ' + (e.response?.data?.message || e.message), 'error'); }
    finally { setBulkClosing(false); }
  };

  const handleBulkAssignSubmit = async () => {
    setBulkAssigning(true);
    try {
      await Promise.all([...selectedTickets].map(tn => nablSupportService.assignTicket(tn, bulkSelectedUserIds)));
      showSnackbar(`Assigned ${selectedTickets.size} tickets`, 'success');
      setBulkAssignDialog(false); setSelectedTickets(new Set()); fetchAssignments();
    } catch (e) { showSnackbar('Bulk assign failed', 'error'); }
    finally { setBulkAssigning(false); }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const r = await nablSupportService.exportTickets();
      if (!r.data?.success) { showSnackbar('Export failed', 'error'); return; }
      const rows = r.data.data || [];
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 4 }, { wch: 14 }, { wch: 50 }, { wch: 30 }, { wch: 10 }, { wch: 12 }, { wch: 20 }, { wch: 10 }, { wch: 12 }, { wch: 25 }, { wch: 25 }, { wch: 20 }, { wch: 50 }, { wch: 60 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Tickets');
      XLSX.writeFile(wb, `NABL_Support_Tickets_${new Date().toISOString().slice(0, 10)}.xlsx`);
      showSnackbar(`Exported ${rows.length} tickets`, 'success');
    } catch (e) { showSnackbar('Export failed: ' + (e.response?.data?.message || e.message), 'error'); }
    finally { setExporting(false); }
  };

  const handleAiChat = async (inputOverride) => {
    const text = (inputOverride ?? aiChatInput).trim();
    if (!text || aiLoading) return;
    const userMsg = { role: 'user', content: text };
    const updatedMsgs = [...aiMessages, userMsg];
    setAiMessages(updatedMsgs);
    setAiChatInput('');
    setAiLoading(true);
    setTimeout(() => aiBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    try {
      const r = await nablSupportService.aiChat(updatedMsgs);
      if (r.data?.success) {
        setAiMessages(prev => [...prev, { role: 'assistant', content: r.data.reply }]);
        setAiProvider(r.data.provider || '');
      } else {
        showSnackbar('AI failed: ' + r.data?.message, 'error');
      }
    } catch (e) {
      showSnackbar('AI failed: ' + (e.response?.data?.message || e.message), 'error');
    } finally {
      setAiLoading(false);
      setTimeout(() => aiBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  };

  const openAiWithContext = (ticket, threadText = '') => {
    setAiChatInput('');
    setAiOpen(true);
  };

  const handleOpenReply = (thread) => {
    const toAddr = thread.fromEmailAddress || selectedTicket?.email || selectedTicket?.fromEmailAddress || '';
    const ccAddr = thread.cc || selectedTicket?.cc || '';
    const subj = selectedTicket?.subject || '';
    setReplyTo(toAddr);
    setReplyCc(ccAddr);
    setReplySubject(subj.toLowerCase().startsWith('re:') ? subj : `Re: ${subj}`);
    setReplyBody('');
    setReplyOpen(true);
  };

  const handleSendReply = async (shouldClose = false) => {
    const plainBody = replyBody.replace(/<[^>]+>/g, '').trim();
    if (!replyTo.trim() || !plainBody) { showSnackbar('To and body are required', 'error'); return; }
    setReplySending(true);
    try {
      const bodyHtml = replyBody;
      await nablSupportService.replyEmail(selectedTicket.ticketNumber, { to: replyTo, cc: replyCc, subject: replySubject, body: bodyHtml });
      if (shouldClose) {
        await nablSupportService.updateTicketStatus(selectedTicket.ticketNumber, 'Closed');
        showSnackbar('Reply sent & ticket closed', 'success');
        setSelectedTicket(p => ({ ...p, status: 'Closed' }));
        fetchAll();
      } else {
        showSnackbar('Reply sent', 'success');
        setSelectedTicket(p => ({ ...p, status: 'Replied' }));
      }
      setReplyOpen(false);
      const r = await nablSupportService.getTicketThreads(selectedTicket.ticketNumber);
      if (r.data?.success) setThreads(r.data.data || []);
    } catch (e) { showSnackbar('Send failed: ' + (e.response?.data?.message || e.message), 'error'); }
    finally { setReplySending(false); }
  };

  const openTicketNums = useMemo(() =>
    new Set([...devTickets, ...supTickets, ...holdTickets].map(t => t.ticketNumber)),
    [devTickets, supTickets, holdTickets]
  );

  const allOpenTickets = useMemo(() =>
    [...sidebarTickets].sort((a, b) => new Date(b.createdTime) - new Date(a.createdTime)),
    [sidebarTickets]
  );

  const sidebarFiltered = useMemo(() => {
    if (!sidebarSearch.trim()) return allOpenTickets;
    const q = sidebarSearch.toLowerCase();
    return allOpenTickets.filter(t =>
      (t.ticketNumber || '').toLowerCase().includes(q) ||
      (t.subject || '').toLowerCase().includes(q) ||
      (t.email || t.fromEmailAddress || '').toLowerCase().includes(q)
    );
  }, [allOpenTickets, sidebarSearch]);

  const handleOpenDelete = (ticket) => { setDeleteTarget(ticket); setDeleteDialog(true); };

  const commonProps = {
    pageSize, onStatusAction: handleStatusAction, onTypeAction: handleTypeAction,
    statusActionLoading, typeActionLoading, assignmentsMap,
    onAssign: handleOpenAssign, onRowClick: handleRowClick,
    selectedTickets, onToggleSelect: handleToggleSelect, onSelectAll: handleSelectAll,
    onDelete: handleOpenDelete, readTickets,
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 2, maxWidth: '100%', bgcolor: T.canvas, minHeight: '100vh' }}>

      {/* Header */}
      <Paper elevation={0} sx={{ bgcolor: T.navy, color: '#fff', borderRadius: 2.5, mb: 2, px: 2.5, py: 1.75, overflow: 'hidden', position: 'relative' }}>
        <Box sx={{ position: 'absolute', right: -24, top: -24, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', right: 40, bottom: -32, width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 2, p: 1, display: 'flex' }}>
              <TicketIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em' }}>NABL Support Tickets</Typography>
              <Typography sx={{ fontSize: '0.72rem', opacity: 0.65, mt: 0.15 }}>
                Gmail · Last synced: {statsLoading ? '…' : stats.lastSyncTime ? new Date(stats.lastSyncTime).toLocaleString('en-IN') : 'Never'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormGroup row>
              <FormControlLabel
                control={<Switch size="small" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} sx={{ '& .MuiSwitch-thumb': { bgcolor: '#fff' } }} />}
                label={<Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.75)' }}>Auto (5m)</Typography>}
              />
            </FormGroup>
            <Button size="small" variant="outlined" startIcon={<RefreshIcon sx={{ fontSize: 14 }} />} onClick={fetchAll}
              sx={{ color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.25)', fontSize: '0.75rem', textTransform: 'none', borderRadius: 1.5, '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.08)' } }}>
              Refresh
            </Button>
            <Button size="small" variant="outlined" startIcon={exporting ? <CircularProgress size={13} color="inherit" /> : <DownloadIcon sx={{ fontSize: 14 }} />} onClick={handleExportExcel} disabled={exporting}
              sx={{ color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.25)', fontSize: '0.75rem', textTransform: 'none', borderRadius: 1.5, '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.08)' } }}>
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
            <Button size="small" variant="contained" startIcon={syncing ? <CircularProgress size={13} color="inherit" /> : <SyncIcon sx={{ fontSize: 14 }} />} onClick={handleSync} disabled={syncing}
              sx={{ bgcolor: T.accent, '&:hover': { bgcolor: '#A93B22' }, fontSize: '0.75rem', textTransform: 'none', borderRadius: 1.5, fontWeight: 600, boxShadow: 'none' }}>
              {syncing ? 'Syncing…' : 'Sync Gmail'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 2, mb: 2, border: `1px solid ${T.borderLight}`, bgcolor: T.surface }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
          sx={{ minHeight: 44, '& .MuiTabs-indicator': { bgcolor: T.navy, height: 2.5 }, borderBottom: `1px solid ${T.borderLight}` }}>
          <Tab label="Dashboard" icon={<TicketIcon sx={{ fontSize: 15 }} />} iconPosition="start"
            sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', color: T.textSec, '&.Mui-selected': { color: T.navy } }} />
          <Tab label="Analytics" icon={<AnalyticsIcon sx={{ fontSize: 15 }} />} iconPosition="start"
            sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', color: T.textSec, '&.Mui-selected': { color: T.navy } }} />
        </Tabs>
      </Paper>

      {/* ── Dashboard Tab ── */}
      {activeTab === 0 && (
        <>
          {/* Stat cards */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[
              { label: 'Open',         value: stats.openTickets,   icon: <HourglassEmptyIcon />, color: '#C45A3D' },
              { label: 'On Hold',      value: stats.onHoldTickets, icon: <HoldIcon />,            color: '#B07A2A' },
              { label: 'Development',  value: devTotal,             icon: <DevIcon />,             color: '#7A5EA8' },
              { label: 'Support',      value: supTotal,             icon: <SupportIcon />,         color: '#3D7A9E' },
              { label: 'Closed Today', value: stats.closedToday,   icon: <TodayIcon />,           color: '#3D7A52' },
              { label: 'Avg Age',      value: stats.avgOpenAgeDays != null ? `${Math.round(stats.avgOpenAgeDays)}d` : '—', icon: <ScheduleIcon />, color: '#7A6050' },
            ].map(c => (
              <Grid key={c.label} item xs={6} sm={4} md={2}>
                <StatCard {...c} />
              </Grid>
            ))}
          </Grid>

          {/* Workload */}
          <WorkloadMini assignmentsMap={assignmentsMap} openTicketNums={openTicketNums} />

          {/* Filter bar */}
          <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 2.5, border: `1px solid ${T.borderLight}`, bgcolor: T.surface }}>
            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField size="small" placeholder="Search ticket #, email, subject…" value={search}
                onChange={e => setSearch(e.target.value)} onKeyPress={e => e.key === 'Enter' && fetchAll()}
                sx={{ flex: 1, minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 16, color: T.textDis, mr: 0.5 }} /> }} />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>Assignee</InputLabel>
                <Select value={assigneeFilter} label="Assignee" onChange={e => setAssigneeFilter(e.target.value)} sx={{ borderRadius: 1.5, fontSize: '0.8rem' }}>
                  <MenuItem value="">All</MenuItem>
                  {allUsers.map(u => <MenuItem key={u.id} value={u.id} sx={{ fontSize: '0.8rem' }}>{u.name}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} sx={{ width: 145, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
              <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={dateTo} onChange={e => setDateTo(e.target.value)} sx={{ width: 145, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
              <Button size="small" variant="contained" startIcon={<SearchIcon sx={{ fontSize: 14 }} />} onClick={fetchAll}
                sx={{ bgcolor: T.navy, '&:hover': { bgcolor: T.navyLight }, borderRadius: 1.5, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', boxShadow: 'none' }}>
                Search
              </Button>
              <Button size="small" variant="outlined" onClick={() => { setSearch(''); setAssigneeFilter(''); setDateFrom(''); setDateTo(''); }}
                sx={{ borderColor: T.border, color: T.textSec, borderRadius: 1.5, textTransform: 'none', fontSize: '0.78rem', '&:hover': { borderColor: T.textSec, bgcolor: T.surfaceAlt } }}>
                Reset
              </Button>
            </Box>
          </Paper>

          {/* Bulk action bar */}
          {selectedTickets.size > 0 && (
            <Paper elevation={0} sx={{ px: 2, py: 1.25, mb: 2, borderRadius: 2, bgcolor: T.navy, color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5, border: `1px solid rgba(255,255,255,0.1)` }}>
              <CheckBoxIcon sx={{ fontSize: 16, opacity: 0.8 }} />
              <Typography sx={{ fontWeight: 700, flex: 1, fontSize: '0.82rem' }}>{selectedTickets.size} ticket{selectedTickets.size !== 1 ? 's' : ''} selected</Typography>
              <Button size="small" variant="outlined" startIcon={<AssignIcon sx={{ fontSize: 13 }} />}
                onClick={() => { setBulkSelectedUserIds([]); setBulkAssignDialog(true); }}
                sx={{ color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', textTransform: 'none', borderRadius: 1.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                Assign All
              </Button>
              <Button size="small" variant="contained" startIcon={bulkClosing ? <CircularProgress size={12} color="inherit" /> : <LockIcon sx={{ fontSize: 13 }} />}
                onClick={handleBulkClose} disabled={bulkClosing}
                sx={{ bgcolor: '#3D7A52', '&:hover': { bgcolor: '#2D5E3D' }, fontSize: '0.75rem', textTransform: 'none', borderRadius: 1.5, boxShadow: 'none', fontWeight: 600 }}>
                Close All
              </Button>
              <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }} onClick={() => setSelectedTickets(new Set())}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Paper>
          )}

          {/* Dev + Support */}
          <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
            <Grid item xs={12} lg={6}>
              <TicketTable title="Development Tickets" headerColor="#7A5EA8" tickets={devTickets} loading={devLoading} total={devTotal} page={devPage} totalPages={devTotalPages} onPageChange={pg => fetchDevTickets(pg)} sortField={devSort.field} sortDir={devSort.dir} onSort={f => setDevSort(p => toggleSort(p, f))} {...commonProps} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TicketTable title="Support Tickets" headerColor="#3D7A9E" tickets={supTickets} loading={supLoading} total={supTotal} page={supPage} totalPages={supTotalPages} onPageChange={pg => fetchSupTickets(pg)} sortField={supSort.field} sortDir={supSort.dir} onSort={f => setSupSort(p => toggleSort(p, f))} {...commonProps} />
            </Grid>
          </Grid>

          {/* On Hold */}
          {(holdTotal > 0 || holdLoading) && (
            <Box sx={{ mb: 2 }}>
              <TicketTable title="On Hold Tickets" headerColor="#B07A2A" tickets={holdTickets} loading={holdLoading} total={holdTotal} page={holdPage} totalPages={holdTotalPages} onPageChange={pg => fetchHoldTickets(pg)} sortField={holdSort.field} sortDir={holdSort.dir} onSort={f => setHoldSort(p => toggleSort(p, f))} {...commonProps} />
            </Box>
          )}

          {/* Closed */}
          <TicketTable title="Closed Tickets" headerColor="#3D7A52" tickets={closedTickets} loading={closedLoading} total={closedTotal} page={closedPage} totalPages={closedTotalPages} onPageChange={pg => fetchClosedTickets(pg)} sortField={closedSort.field} sortDir={closedSort.dir} onSort={f => setClosedSort(p => toggleSort(p, f))} {...commonProps} />
        </>
      )}

      {/* ── Analytics Tab ── */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
            <Button size="small" variant="outlined" startIcon={analyticsLoading ? <CircularProgress size={13} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
              onClick={fetchAnalytics} disabled={analyticsLoading}
              sx={{ borderColor: T.border, color: T.textSec, borderRadius: 1.5, textTransform: 'none', fontSize: '0.78rem', '&:hover': { borderColor: T.navy, color: T.navy } }}>
              {analyticsLoading ? 'Loading…' : 'Reload Analytics'}
            </Button>
          </Box>
          <AnalyticsTab analytics={analytics} loading={analyticsLoading} />
        </Box>
      )}

      {/* Single Assign Dialog */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${T.borderLight}` }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: T.textPri }}>Assign #{assignTarget?.ticketNumber}</Typography>
          <IconButton size="small" onClick={() => setAssignDialog(false)} sx={{ color: T.textDis }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5, pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            {allUsers.map((u, i) => (
              <Box key={u.id} onClick={() => setSelectedUserIds(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])}
                sx={{ px: 1.5, py: 0.9, cursor: 'pointer', borderRadius: 1.5, border: `1px solid ${selectedUserIds.includes(u.id) ? '#5C6BC0' : T.border}`,
                  bgcolor: selectedUserIds.includes(u.id) ? '#EEF0FA' : T.surface, display: 'flex', alignItems: 'center', gap: 1,
                  transition: 'all 0.12s', '&:hover': { bgcolor: selectedUserIds.includes(u.id) ? '#E5E8F7' : T.surfaceAlt } }}>
                <Checkbox checked={selectedUserIds.includes(u.id)} size="small" sx={{ p: 0, color: '#5C6BC0' }} onChange={() => {}} />
                <Avatar sx={{ width: 26, height: 26, fontSize: '0.68rem', fontWeight: 700, bgcolor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>{u.name[0].toUpperCase()}</Avatar>
                <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: T.textPri }}>{u.name}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5, borderTop: `1px solid ${T.borderLight}` }}>
          <Button onClick={() => { setAssignDialog(false); if (!replyFlowActive) return; setReplyFlowActive(false); setReplyOpen(true); }} size="small" sx={{ textTransform: 'none', color: T.textSec, borderRadius: 1.5 }}>
            {replyFlowActive ? 'Skip' : 'Cancel'}
          </Button>
          <Button onClick={replyFlowActive ? advanceToRemarks : () => setAssignDialog(false)} variant="outlined" size="small"
            sx={{ textTransform: 'none', borderRadius: 1.5, borderColor: T.border, color: T.textSec, display: replyFlowActive ? 'flex' : 'none' }}>
            Assign &amp; Continue
          </Button>
          <Button onClick={handleAssignSubmit} variant="contained" size="small" disabled={assigning} startIcon={assigning ? <CircularProgress size={13} color="inherit" /> : <AssignIcon sx={{ fontSize: 14 }} />}
            sx={{ bgcolor: T.navy, '&:hover': { bgcolor: T.navyLight }, textTransform: 'none', borderRadius: 1.5, boxShadow: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
            {assigning ? 'Assigning…' : replyFlowActive ? `Assign (${selectedUserIds.length}) & Continue` : `Assign (${selectedUserIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={bulkAssignDialog} onClose={() => setBulkAssignDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${T.borderLight}` }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: T.textPri }}>Bulk Assign — {selectedTickets.size} tickets</Typography>
          <IconButton size="small" onClick={() => setBulkAssignDialog(false)} sx={{ color: T.textDis }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5, pb: 1 }}>
          <Typography sx={{ fontSize: '0.78rem', color: T.textSec, mb: 1.25 }}>Select user(s) — replaces existing assignments on all selected tickets.</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            {allUsers.map((u, i) => (
              <Box key={u.id} onClick={() => setBulkSelectedUserIds(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])}
                sx={{ px: 1.5, py: 0.9, cursor: 'pointer', borderRadius: 1.5, border: `1px solid ${bulkSelectedUserIds.includes(u.id) ? '#5C6BC0' : T.border}`,
                  bgcolor: bulkSelectedUserIds.includes(u.id) ? '#EEF0FA' : T.surface, display: 'flex', alignItems: 'center', gap: 1,
                  transition: 'all 0.12s', '&:hover': { bgcolor: bulkSelectedUserIds.includes(u.id) ? '#E5E8F7' : T.surfaceAlt } }}>
                <Checkbox checked={bulkSelectedUserIds.includes(u.id)} size="small" sx={{ p: 0, color: '#5C6BC0' }} onChange={() => {}} />
                <Avatar sx={{ width: 26, height: 26, fontSize: '0.68rem', fontWeight: 700, bgcolor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>{u.name[0].toUpperCase()}</Avatar>
                <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: T.textPri }}>{u.name}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5, borderTop: `1px solid ${T.borderLight}` }}>
          <Button onClick={() => setBulkAssignDialog(false)} size="small" sx={{ textTransform: 'none', color: T.textSec, borderRadius: 1.5 }}>Cancel</Button>
          <Button onClick={handleBulkAssignSubmit} variant="contained" size="small" disabled={bulkAssigning || !bulkSelectedUserIds.length} startIcon={bulkAssigning ? <CircularProgress size={13} color="inherit" /> : <AssignIcon sx={{ fontSize: 14 }} />}
            sx={{ bgcolor: T.navy, '&:hover': { bgcolor: T.navyLight }, textTransform: 'none', borderRadius: 1.5, boxShadow: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
            {bulkAssigning ? 'Assigning…' : `Assign (${bulkSelectedUserIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Full-Screen View */}
      <Dialog fullScreen open={drawerOpen} onClose={() => setDrawerOpen(false)} sx={{ zIndex: 1300 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: T.navy, color: '#fff', px: 2, py: 1.25, flexShrink: 0 }}>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'rgba(255,255,255,0.75)', '&:hover': { color: '#fff' } }} size="small"><ChevronLeftIcon /></IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>#{selectedTicket?.ticketNumber}</Typography>
              {selectedTicket && <Chip label={selectedTicket.status} size="small" color={STATUS_COLOR[selectedTicket.status] || 'default'} sx={{ height: 17, fontSize: '0.58rem' }} />}
            </Box>
            <Typography sx={{ fontSize: '0.72rem', opacity: 0.65, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedTicket?.subject}</Typography>
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'rgba(255,255,255,0.75)', '&:hover': { color: '#fff' } }} size="small"><CloseIcon /></IconButton>
        </Box>

        {/* Body: left list + right detail */}
        <Box sx={{ display: 'flex', height: 'calc(100vh - 56px)', overflow: 'hidden', bgcolor: T.canvas }}>

          {/* Left: open ticket list */}
          <Box sx={{ width: 290, flexShrink: 0, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', bgcolor: T.surfaceWarm }}>
            <Box sx={{ p: 1.25, borderBottom: `1px solid ${T.borderLight}` }}>
              <TextField
                size="small" fullWidth
                placeholder="Search open tickets…"
                value={sidebarSearch}
                onChange={e => setSidebarSearch(e.target.value)}
                InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 15, color: T.textDis, mr: 0.5 }} /> }}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.75rem', borderRadius: 1.5 } }}
              />
              <Typography sx={{ fontSize: '0.65rem', color: T.textDis, mt: 0.5, display: 'block' }}>
                {sidebarFiltered.length} open ticket{sidebarFiltered.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {sidebarFiltered.map(t => {
                const age = getAgePriority(t.createdTime);
                const isActive = selectedTicket?.ticketNumber === t.ticketNumber;
                const isSidebarRead = readTickets.has(t.ticketNumber);
                return (
                  <Box
                    key={t.ticketNumber}
                    onClick={() => handleRowClick(t)}
                    sx={{
                      px: 1.5, py: 1, cursor: 'pointer', borderBottom: `1px solid ${T.borderLight}`,
                      borderLeft: `3px solid ${isActive ? '#5C6BC0' : age.border}`,
                      bgcolor: isActive ? '#EEF3FB' : T.surfaceWarm,
                      transition: 'background-color 0.12s',
                      '&:hover': { bgcolor: isActive ? '#E5EDF9' : T.surfaceAlt },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
                      {!isSidebarRead && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4A6FA5', flexShrink: 0 }} />}
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: isSidebarRead ? 400 : 700, color: isActive ? '#4A6FA5' : isSidebarRead ? T.textSec : T.textPri, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject || '—'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: T.textSec }}>{t.ticketNumber}</Typography>
                      <Chip label={age.label} size="small" color={age.chipColor} sx={{ height: 14, fontSize: '0.55rem', fontWeight: 700 }} />
                      <Chip label={t.ticket_type === 'Development' ? 'Dev' : 'Sup'} size="small"
                        sx={{ height: 14, fontSize: '0.54rem', bgcolor: t.ticket_type === 'Development' ? '#7A5EA8' : '#3D7A9E', color: '#fff', fontWeight: 600 }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.62rem', color: T.textDis, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.email || t.fromEmailAddress || ''}</Typography>
                  </Box>
                );
              })}
              {sidebarFiltered.length === 0 && (
                <Typography sx={{ fontSize: '0.75rem', color: T.textDis, display: 'block', textAlign: 'center', mt: 3 }}>No open tickets</Typography>
              )}
            </Box>
          </Box>

          {/* Right: ticket detail */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5, bgcolor: T.canvas }}>
            {selectedTicket && (
              <>
                <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: T.surface, border: `1px solid ${T.borderLight}`, borderRadius: 2.5 }}>
                  <Grid container spacing={1.25}>
                    <Grid item xs={7}>
                      <Typography sx={{ fontSize: '0.63rem', fontWeight: 600, color: T.textDis, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>From</Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: T.textPri, fontWeight: 500 }}>{selectedTicket.email || selectedTicket.fromEmailAddress || '—'}</Typography>
                    </Grid>
                    <Grid item xs={2.5}>
                      <Typography sx={{ fontSize: '0.63rem', fontWeight: 600, color: T.textDis, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>Status</Typography>
                      <Box><Chip label={selectedTicket.status} size="small" color={STATUS_COLOR[selectedTicket.status] || 'default'} sx={{ height: 18, fontSize: '0.62rem' }} /></Box>
                    </Grid>
                    <Grid item xs={2.5}>
                      <Typography sx={{ fontSize: '0.63rem', fontWeight: 600, color: T.textDis, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>Type</Typography>
                      <Box><Chip label={selectedTicket.ticket_type || 'Support'} size="small" color={selectedTicket.ticket_type === 'Development' ? 'secondary' : 'primary'} sx={{ height: 18, fontSize: '0.62rem' }} /></Box>
                    </Grid>
                    {selectedTicket.cc && (
                      <Grid item xs={12}>
                        <Typography sx={{ fontSize: '0.63rem', fontWeight: 600, color: T.textDis, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>CC</Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: T.textSec }}>{selectedTicket.cc}</Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: '0.63rem', fontWeight: 600, color: T.textDis, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Assigned To</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(assignmentsMap[selectedTicket.ticketNumber] || []).map((u, i) => (
                          <Chip key={u.user_id} label={u.name} size="small" avatar={<Avatar sx={{ bgcolor: AVATAR_COLORS[i % AVATAR_COLORS.length], fontSize: '0.6rem' }}>{u.name[0]}</Avatar>}
                            sx={{ fontSize: '0.72rem', height: 22, bgcolor: T.surfaceAlt, border: `1px solid ${T.border}` }} />
                        ))}
                        {!(assignmentsMap[selectedTicket.ticketNumber] || []).length && <Typography sx={{ fontSize: '0.72rem', color: T.textDis }}>Unassigned</Typography>}
                      </Box>
                    </Grid>
                    {selectedTicket.webUrl && (
                      <Grid item xs={12}>
                        <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />} href={selectedTicket.webUrl} target="_blank" rel="noreferrer"
                          sx={{ fontSize: '0.72rem', textTransform: 'none', color: '#4A6FA5', p: 0 }}>
                          Open in Gmail
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Paper>

                <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
                  {(() => {
                    const isOpen = selectedTicket.status === 'Open' || selectedTicket.status === 'On Hold';
                    return (
                      <Button size="small" variant="contained"
                        startIcon={statusActionLoading === selectedTicket.ticketNumber ? <CircularProgress size={13} color="inherit" /> : isOpen ? <LockIcon sx={{ fontSize: 14 }} /> : <LockOpenIcon sx={{ fontSize: 14 }} />}
                        disabled={statusActionLoading === selectedTicket.ticketNumber}
                        onClick={isOpen ? handleOpenCloseDialog : async () => { await handleStatusAction(selectedTicket.ticketNumber, 'Open'); setSelectedTicket(p => ({ ...p, status: 'Open' })); }}
                        sx={{ bgcolor: isOpen ? '#3D7A52' : '#B05050', '&:hover': { bgcolor: isOpen ? '#2D5E3D' : '#8B3B3B' }, fontSize: '0.75rem', textTransform: 'none', borderRadius: 1.5, boxShadow: 'none', fontWeight: 600 }}>
                        {statusActionLoading === selectedTicket.ticketNumber ? 'Updating…' : isOpen ? 'Close Ticket' : 'Reopen Ticket'}
                      </Button>
                    );
                  })()}
                  <Button size="small" variant="outlined" startIcon={<AssignIcon sx={{ fontSize: 14 }} />} onClick={() => handleOpenAssign(selectedTicket)}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', borderRadius: 1.5, borderColor: T.border, color: T.textSec, '&:hover': { borderColor: '#5C6BC0', color: '#5C6BC0' } }}>
                    Assign
                  </Button>
                  {(() => {
                    const isDev = selectedTicket.ticket_type === 'Development';
                    return (
                      <Button size="small" variant="outlined"
                        startIcon={typeActionLoading === selectedTicket.ticketNumber ? <CircularProgress size={13} color="inherit" /> : isDev ? <SupportIcon sx={{ fontSize: 14 }} /> : <DevIcon sx={{ fontSize: 14 }} />}
                        disabled={typeActionLoading === selectedTicket.ticketNumber}
                        onClick={async () => { const t = isDev ? 'Support' : 'Development'; await handleTypeAction(selectedTicket.ticketNumber, t); setSelectedTicket(p => ({ ...p, ticket_type: t })); }}
                        sx={{ fontSize: '0.75rem', textTransform: 'none', borderRadius: 1.5, borderColor: T.border, color: T.textSec, '&:hover': { borderColor: '#8B5AA8', color: '#8B5AA8' } }}>
                        {typeActionLoading === selectedTicket.ticketNumber ? '…' : isDev ? 'Move to Support' : 'Mark as Dev'}
                      </Button>
                    );
                  })()}
                  <Button size="small" variant="contained" startIcon={<ReplyIcon sx={{ fontSize: 14 }} />}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', borderRadius: 1.5, bgcolor: '#4A6FA5', '&:hover': { bgcolor: '#3A5A8A' }, boxShadow: 'none', fontWeight: 600 }}
                    onClick={() => {
                      const latestIn = [...threads].reverse().find(th => th.direction === 'in');
                      const th = latestIn || { fromEmailAddress: selectedTicket?.email || selectedTicket?.fromEmailAddress, cc: selectedTicket?.cc };
                      const subj = selectedTicket?.subject || '';
                      setReplyTo(th.fromEmailAddress || '');
                      setReplyCc(th.cc || selectedTicket?.cc || '');
                      setReplySubject(subj.toLowerCase().startsWith('re:') ? subj : `Re: ${subj}`);
                      setReplyBody('');
                      setReplyFlowActive(true);
                      handleOpenAssign(selectedTicket);
                    }}>
                    Quick Reply
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<AiIcon sx={{ fontSize: 14 }} />}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', borderRadius: 1.5, borderColor: T.border, color: '#7A5EA8', '&:hover': { borderColor: '#7A5EA8', bgcolor: '#F8F4FF' } }}
                    onClick={() => {
                      const last = [...threads].reverse().find(th => th.direction === 'in');
                      const txt = last?.content?.replace(/<[^>]+>/g, '') || last?.summary || '';
                      openAiWithContext(selectedTicket, txt.slice(0, 600));
                    }}>
                    AI Reply
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', borderRadius: 1.5, borderColor: '#D4817E', color: '#B05050', '&:hover': { borderColor: '#B05050', bgcolor: '#FCEAEA' }, ml: 'auto' }}
                    onClick={() => handleOpenDelete(selectedTicket)}>
                    Delete
                  </Button>
                </Box>
              </>
            )}

            {/* Notes */}
            <Paper elevation={0} sx={{ p: 1.75, mb: 2, bgcolor: '#FDFAF3', border: `1px solid #E5D98E`, borderRadius: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <NoteIcon sx={{ fontSize: 15, color: '#B08A30' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#7A5E1A' }}>Corrective Action</Typography>
              </Box>
              <TextField
                multiline minRows={3} maxRows={8} fullWidth size="small"
                placeholder="Describe the corrective action — what to do, context, follow-up actions…"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                sx={{ bgcolor: T.surface, borderRadius: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button size="small" variant="contained"
                  startIcon={noteSaving ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: 13 }} />}
                  onClick={handleSaveNote} disabled={noteSaving}
                  sx={{ bgcolor: '#B08A30', '&:hover': { bgcolor: '#8A6B20' }, fontSize: '0.75rem', textTransform: 'none', borderRadius: 1.5, boxShadow: 'none', fontWeight: 600 }}>
                  {noteSaving ? 'Saving…' : 'Save Note'}
                </Button>
              </Box>
            </Paper>


            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: T.textPri }}>Threads</Typography>
              <Chip label={threads.length} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: T.surfaceAlt, color: T.textSec, border: `1px solid ${T.border}` }} />
            </Box>
            <Divider sx={{ mb: 1.5, borderColor: T.borderLight }} />

            {threadsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress sx={{ color: T.navy }} /></Box>
            ) : threads.length === 0 ? (
              <Typography sx={{ fontSize: '0.82rem', color: T.textDis, textAlign: 'center', py: 4 }}>No threads found</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {threads.map((th, i) => {
                  const isIn = th.direction === 'in';
                  const attachments = (() => { try { return JSON.parse(th.attachments_json || '[]'); } catch { return []; } })();
                  return (
                    <Paper key={th.id || i} elevation={0} sx={{
                      p: 1.75, borderRadius: 2,
                      borderLeft: `3px solid ${isIn ? T.threadInBd : T.threadOutBd}`,
                      bgcolor: th.isDescriptionThread === 'true' ? '#FDFAF3' : isIn ? T.threadInBg : T.threadOutBg,
                      border: `1px solid ${T.borderLight}`,
                      borderLeftWidth: 3,
                      borderLeftColor: isIn ? T.threadInBd : T.threadOutBd,
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ bgcolor: isIn ? T.threadInBd + '22' : T.threadOutBd + '22', borderRadius: 1, p: 0.4, display: 'flex' }}>
                            <EmailIcon sx={{ fontSize: 13, color: isIn ? T.threadInBd : T.threadOutBd }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.73rem', fontWeight: 700, color: T.textPri }}>{th.fromEmailAddress || '—'}</Typography>
                          {th.isDescriptionThread === 'true' && <Chip label="Desc" size="small" color="warning" sx={{ height: 15, fontSize: '0.58rem' }} />}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Chip label={isIn ? 'Received' : 'Sent'} size="small" variant="outlined"
                            sx={{ height: 15, fontSize: '0.58rem', fontWeight: 600,
                              color: isIn ? T.threadInBd : T.threadOutBd,
                              borderColor: isIn ? T.threadInBd + '60' : T.threadOutBd + '60' }} />
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: '0.63rem', color: T.textDis, mb: th.cc ? 0.25 : 0.75 }}>
                        {th.createdTime ? new Date(th.createdTime).toLocaleString('en-IN') : ''}{th.to && <> · To: {th.to}</>}
                      </Typography>
                      {th.cc && (
                        <Typography sx={{ fontSize: '0.62rem', color: T.textDis, mb: 0.75 }}>CC: {th.cc}</Typography>
                      )}
                      {th.content
                        ? <EmailFrame html={th.content} />
                        : th.summary
                          ? <Typography sx={{ fontSize: '0.75rem', fontStyle: 'italic', color: 'text.secondary' }}>{th.summary}</Typography>
                          : null}

                      {/* Attachments */}
                      {attachments.length > 0 && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${T.borderLight}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                            <AttachIcon sx={{ fontSize: 13, color: T.textSec }} />
                            <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, color: T.textSec }}>
                              {attachments.length} Attachment{attachments.length !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {attachments.map((att, ai) => {
                              const key = `${th.thread_id}_${att.attachmentId}`;
                              const isImg = att.mimeType?.startsWith('image/');
                              const isPdf = att.mimeType === 'application/pdf';
                              const icon = isImg ? <ImageIcon sx={{ fontSize: 13 }} /> : isPdf ? <PdfIcon sx={{ fontSize: 13 }} /> : <FileIcon sx={{ fontSize: 13 }} />;
                              const sizeKb = att.size ? `${Math.round(att.size / 1024)}KB` : '';
                              const attColor = isImg ? '#3D7A9E' : isPdf ? '#B05050' : T.textSec;
                              return (
                                <Tooltip key={ai} title={`${att.filename} ${sizeKb ? `(${sizeKb})` : ''}`}>
                                  <Button size="small" variant="outlined"
                                    startIcon={attachmentLoading === key ? <CircularProgress size={11} /> : icon}
                                    disabled={attachmentLoading === key}
                                    onClick={() => handleDownloadAttachment(th.thread_id, att.attachmentId, att.filename, att.mimeType)}
                                    sx={{ fontSize: '0.63rem', py: 0.25, px: 0.75, textTransform: 'none', borderRadius: 1.25,
                                      maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                      borderColor: attColor + '50', color: attColor, '&:hover': { bgcolor: attColor + '10', borderColor: attColor } }}>
                                    {att.filename}
                                  </Button>
                                </Tooltip>
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {/* Reply button */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.25, pt: 0.75, borderTop: `1px solid ${T.borderLight}` }}>
                        <Button size="small" variant="outlined" startIcon={<ReplyIcon sx={{ fontSize: 13 }} />}
                          onClick={() => handleOpenReply(th)}
                          sx={{ fontSize: '0.7rem', py: 0.3, px: 1.25, textTransform: 'none', borderRadius: 1.5,
                            color: '#4A6FA5', borderColor: '#4A6FA5' + '60', '&:hover': { bgcolor: '#EEF3FB', borderColor: '#4A6FA5' } }}>
                          Reply
                        </Button>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Dialog>

      {/* Remarks Dialog — step 2 of Quick Reply flow */}
      <Dialog open={replyRemarksDialog} onClose={() => { setReplyRemarksDialog(false); setReplyFlowActive(false); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ pb: 1.25, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${T.borderLight}` }}>
          <Box sx={{ bgcolor: '#FDFAF3', borderRadius: 1.5, p: 0.6, display: 'flex' }}>
            <NoteIcon sx={{ color: '#B08A30', fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: T.textPri }}>Add Remarks</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: T.textDis }}>Internal note before replying — #{selectedTicket?.ticketNumber}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField multiline minRows={4} fullWidth size="small" placeholder="Add internal remarks or notes before sending reply…"
            value={replyRemarks} onChange={e => setReplyRemarks(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, borderTop: `1px solid ${T.borderLight}`, gap: 0.75 }}>
          <Button size="small" onClick={() => { setReplyRemarksDialog(false); setReplyFlowActive(false); setReplyOpen(true); }}
            sx={{ textTransform: 'none', color: T.textSec, borderRadius: 1.5 }}>Skip</Button>
          <Button variant="contained" size="small" onClick={advanceToReply} disabled={remarksSaving}
            startIcon={remarksSaving ? <CircularProgress size={13} color="inherit" /> : <ReplyIcon sx={{ fontSize: 14 }} />}
            sx={{ bgcolor: '#4A6FA5', '&:hover': { bgcolor: '#3A5A8A' }, textTransform: 'none', borderRadius: 1.5, boxShadow: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
            {remarksSaving ? 'Saving…' : 'Save & Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyOpen} onClose={() => !replySending && setReplyOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ pb: 1.25, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${T.borderLight}` }}>
          <Box sx={{ bgcolor: '#EEF3FB', borderRadius: 1.5, p: 0.6, display: 'flex' }}>
            <ReplyIcon sx={{ color: '#4A6FA5', fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: T.textPri }}>Reply</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: T.textDis }}>#{selectedTicket?.ticketNumber}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setReplyOpen(false)} disabled={replySending} sx={{ color: T.textDis }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField label="To" size="small" fullWidth required value={replyTo} onChange={e => setReplyTo(e.target.value)}
              placeholder="recipient@example.com" error={replySending && !replyTo.trim()}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
            <TextField label="CC" size="small" fullWidth value={replyCc} onChange={e => setReplyCc(e.target.value)}
              placeholder="cc1@example.com, cc2@example.com"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
            <TextField label="Subject" size="small" fullWidth value={replySubject} onChange={e => setReplySubject(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
            <Box sx={{
              border: `1px solid ${replySending && !replyBody.replace(/<[^>]+>/g,'').trim() ? '#d32f2f' : 'rgba(0,0,0,0.23)'}`,
              borderRadius: 1.5, overflow: 'hidden',
              '& .ql-toolbar': { borderBottom: '1px solid rgba(0,0,0,0.12)', bgcolor: '#FAFAFA' },
              '& .ql-container': { fontSize: '0.85rem', minHeight: 200, maxHeight: 380, overflowY: 'auto', border: 'none' },
              '& .ql-editor': { minHeight: 200 },
              '&:hover': { borderColor: 'rgba(0,0,0,0.87)' },
            }}>
              <ReactQuill
                theme="snow"
                value={replyBody}
                onChange={setReplyBody}
                modules={REPLY_QUILL_MODULES}
                formats={REPLY_QUILL_FORMATS}
                placeholder="Type your reply here… (paste screenshots, format text, add links)"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, borderTop: `1px solid ${T.borderLight}`, gap: 0.75 }}>
          <Button onClick={() => setReplyOpen(false)} disabled={replySending} size="small"
            sx={{ textTransform: 'none', color: T.textSec, borderRadius: 1.5 }}>Cancel</Button>
          <Button variant="outlined" size="small"
            startIcon={replySending ? <CircularProgress size={13} color="inherit" /> : <ReplyIcon sx={{ fontSize: 14 }} />}
            onClick={() => handleSendReply(false)} disabled={replySending}
            sx={{ borderColor: '#4A6FA5', color: '#4A6FA5', '&:hover': { bgcolor: '#EEF3FB', borderColor: '#3A5A8A' }, textTransform: 'none', borderRadius: 1.5, fontWeight: 600, fontSize: '0.78rem' }}>
            {replySending ? 'Sending…' : 'Reply'}
          </Button>
          <Button variant="contained" size="small"
            startIcon={replySending ? <CircularProgress size={13} color="inherit" /> : <LockIcon sx={{ fontSize: 14 }} />}
            onClick={() => handleSendReply(true)} disabled={replySending}
            sx={{ bgcolor: '#3D7A52', '&:hover': { bgcolor: '#2D5E3D' }, textTransform: 'none', borderRadius: 1.5, boxShadow: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
            {replySending ? 'Sending…' : 'Reply & Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Ticket Dialog */}
      <Dialog open={closeTicketDialog} onClose={() => !closeTicketLoading && setCloseTicketDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ pb: 1.25, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${T.borderLight}` }}>
          <Box sx={{ bgcolor: '#EDF6F1', borderRadius: 1.5, p: 0.6, display: 'flex' }}>
            <LockIcon sx={{ color: '#3D7A52', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: T.textPri }}>Close Ticket</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: T.textDis }}>#{selectedTicket?.ticketNumber}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField label="Corrective Action" multiline minRows={3} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            value={closeCorrectiveAction} onChange={e => setCloseCorrectiveAction(e.target.value)}
            placeholder="Describe the corrective action taken to resolve the issue…"
            required error={closeTicketLoading && !closeCorrectiveAction.trim()} />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, borderTop: `1px solid ${T.borderLight}`, gap: 0.75 }}>
          <Button onClick={() => setCloseTicketDialog(false)} disabled={closeTicketLoading} size="small"
            sx={{ textTransform: 'none', color: T.textSec, borderRadius: 1.5 }}>Cancel</Button>
          <Button variant="contained" size="small"
            startIcon={closeTicketLoading ? <CircularProgress size={13} color="inherit" /> : <LockIcon sx={{ fontSize: 14 }} />}
            onClick={handleConfirmClose} disabled={closeTicketLoading}
            sx={{ bgcolor: '#3D7A52', '&:hover': { bgcolor: '#2D5E3D' }, textTransform: 'none', borderRadius: 1.5, boxShadow: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
            {closeTicketLoading ? 'Closing…' : 'Close Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Popup */}
      <Dialog open={imagePopup.open} onClose={handleCloseImagePopup} maxWidth="lg" fullWidth
        PaperProps={{ sx: { bgcolor: '#111', m: 1, borderRadius: 2, overflow: 'hidden' } }}>
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1 }}>
          <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5, zIndex: 1 }}>
            <Tooltip title="Download">
              <IconButton size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                onClick={() => { const a = document.createElement('a'); a.href = imagePopup.url; a.download = imagePopup.filename; a.click(); }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                onClick={handleCloseImagePopup}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem', pt: 0.5, pb: 1, maxWidth: '75%', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {imagePopup.filename}
          </Typography>
          <Box component="img" src={imagePopup.url} alt={imagePopup.filename}
            sx={{ maxWidth: '100%', maxHeight: '82vh', objectFit: 'contain', display: 'block', borderRadius: 1 }} />
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => !deleteLoading && setDeleteDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${T.borderLight}` }}>
          <Box sx={{ bgcolor: '#FCEAEA', borderRadius: 1.5, p: 0.6, display: 'flex' }}>
            <DeleteIcon sx={{ color: '#B05050', fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: T.textPri }}>Delete Ticket</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: '0.85rem', color: T.textSec }}>
            Permanently delete ticket <strong>{deleteTarget?.ticketNumber}</strong>?
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: T.textDis, mt: 0.75 }}>
            {deleteTarget?.subject}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#B05050', mt: 1.5, fontWeight: 600 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1.5, borderTop: `1px solid ${T.borderLight}` }}>
          <Button onClick={() => setDeleteDialog(false)} disabled={deleteLoading} size="small" sx={{ textTransform: 'none', color: T.textSec, borderRadius: 1.5 }}>Cancel</Button>
          <Button onClick={handleDeleteTicket} variant="contained" size="small" disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={13} color="inherit" /> : <DeleteIcon sx={{ fontSize: 14 }} />}
            sx={{ bgcolor: '#B05050', '&:hover': { bgcolor: '#8B3B3B' }, textTransform: 'none', borderRadius: 1.5, boxShadow: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
            {deleteLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Chat Drawer */}
      <Drawer anchor="right" open={aiOpen} onClose={() => setAiOpen(false)} sx={{ zIndex: 1500 }} PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, display: 'flex', flexDirection: 'column', bgcolor: T.canvas } }}>
        {/* Header */}
        <Box sx={{ px: 2.25, py: 1.5, bgcolor: T.navy, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 1.5, p: 0.6, display: 'flex' }}>
              <AiIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>AI Assistant</Typography>
              <Typography sx={{ fontSize: '0.65rem', opacity: 0.6 }}>{aiProvider || 'Powered by Groq · Llama'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Clear chat">
              <IconButton size="small" onClick={() => { setAiMessages([]); setAiChatInput(''); }} sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}>
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <IconButton onClick={() => setAiOpen(false)} sx={{ color: 'rgba(255,255,255,0.75)', '&:hover': { color: '#fff' } }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>
        </Box>

        {/* Messages */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {aiMessages.length === 0 && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, opacity: 0.5, py: 6 }}>
              <AiIcon sx={{ fontSize: 36, color: '#5C4A8A' }} />
              <Typography sx={{ fontSize: '0.82rem', color: T.textSec, textAlign: 'center' }}>Ask me anything — drafting emails, NABL queries, rephrasing, or general questions.</Typography>
            </Box>
          )}
          {aiMessages.map((msg, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <Box sx={{
                maxWidth: '85%', px: 1.5, py: 1,
                bgcolor: msg.role === 'user' ? '#5C4A8A' : T.surface,
                color: msg.role === 'user' ? '#fff' : T.textPri,
                borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                border: msg.role === 'user' ? 'none' : `1px solid ${T.borderLight}`,
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
              }}>
                <Typography sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{msg.content}</Typography>
                {msg.role === 'assistant' && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
                    <Button size="small" onClick={() => { navigator.clipboard.writeText(msg.content); showSnackbar('Copied!', 'success'); }}
                      sx={{ fontSize: '0.62rem', textTransform: 'none', color: T.textDis, p: 0, minWidth: 0, '&:hover': { color: '#5C4A8A', bgcolor: 'transparent' } }}>
                      Copy
                    </Button>
                    <Button size="small"
                      onClick={() => {
                        setReplyBody(msg.content);
                        if (!replyOpen) setReplyOpen(true);
                        showSnackbar('Pasted to reply', 'success');
                      }}
                      sx={{ fontSize: '0.62rem', textTransform: 'none', color: '#4A6FA5', p: 0, minWidth: 0, '&:hover': { color: '#3A5A8A', bgcolor: 'transparent' } }}>
                      Use in Reply
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
          {aiLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Box sx={{ px: 1.5, py: 1, bgcolor: T.surface, border: `1px solid ${T.borderLight}`, borderRadius: '16px 16px 16px 4px', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CircularProgress size={12} sx={{ color: '#5C4A8A' }} />
                <Typography sx={{ fontSize: '0.75rem', color: T.textDis }}>Thinking…</Typography>
              </Box>
            </Box>
          )}
          <div ref={aiBottomRef} />
        </Box>

        {/* Input */}
        <Box sx={{ p: 1.25, borderTop: `1px solid ${T.borderLight}`, bgcolor: T.surface, display: 'flex', gap: 1, alignItems: 'flex-end', flexShrink: 0 }}>
          <TextField
            multiline maxRows={5} fullWidth size="small"
            placeholder="Ask anything…"
            value={aiChatInput}
            onChange={e => setAiChatInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAiChat(); } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: T.canvas, fontSize: '0.82rem' } }}
          />
          <IconButton
            onClick={() => handleAiChat()}
            disabled={aiLoading || !aiChatInput.trim()}
            sx={{ bgcolor: '#5C4A8A', color: '#fff', borderRadius: 2, p: 1, flexShrink: 0, '&:hover': { bgcolor: '#4A3A72' }, '&.Mui-disabled': { bgcolor: T.border, color: T.textDis } }}>
            <ReplyIcon sx={{ fontSize: 18, transform: 'scaleX(-1)' }} />
          </IconButton>
        </Box>
      </Drawer>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ borderRadius: 1.5, fontSize: '0.82rem' }}>{snackbar.message}</Alert>
      </Snackbar>

      {/* AI FAB */}
      {!aiOpen && (
        <Tooltip title="AI Assistant" placement="left">
          <Fab onClick={() => setAiOpen(true)} size="medium"
            sx={{ position: 'fixed', bottom: 28, right: 28, zIndex: 1500, bgcolor: '#5C4A8A', color: '#fff', boxShadow: '0 4px 16px rgba(92,74,138,0.35)', '&:hover': { bgcolor: '#4A3A72' } }}>
            <AiIcon />
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
}
